import { useCallback, useMemo, useState } from 'react';
import FilterDropdown from 'components/elements/Dropdown/FilterDropdown';
import {
  useDashboardPastProjectStage,
  useDashboardExcludePastProjectStage,
  useUserStateSelection,
} from 'state/dashboard';
import { useEventTriggerDocTypeByStateCode } from 'state/eventTriggers';
import { cityMap } from 'utils/misc';

const PastProjectStageFilter = () => {
  const [savedStages, saveStages] = useDashboardPastProjectStage();
  const [excludeSavedStages, setExcludeSavedStages] =
    useDashboardExcludePastProjectStage();
  const [filter] = useState('');
  const [selectedState] = useUserStateSelection();

  const cityDocumentTypes = useEventTriggerDocTypeByStateCode(selectedState);

  const selectedTriggers = useMemo(() => {
    return savedStages[0] && savedStages[0].documentTypes
      ? cityDocumentTypes.map((cityDoc) => {
          return savedStages[0].documentTypes
            .map((savedDoc) => (savedDoc.id === cityDoc.id ? cityDoc.name : ''))
            .filter((x) => x !== '')
            .map((x) => x)[0];
        })
      : [];
  }, [cityDocumentTypes, savedStages]);

  const excludedStages = useMemo(() => {
    return excludeSavedStages[0] && excludeSavedStages[0].documentTypes
      ? cityDocumentTypes.map((cityDoc) => {
          return excludeSavedStages[0].documentTypes
            .map((savedDoc) => (savedDoc.id === cityDoc.id ? cityDoc.name : ''))
            .filter((x) => x !== '')
            .map((x) => x)[0];
        })
      : [];
  }, [cityDocumentTypes, excludeSavedStages]);

  const createEventTriggerState = useCallback(
    (eventSelections: string[]) => {
      // create event trigger state with selected event types
      const eventTriggerState = {
        cityId: cityMap.AUSTIN,
        documentTypes: eventSelections
          .filter((event) => event !== undefined)
          .map((event) => {
            return {
              id: cityDocumentTypes
                .filter((doc) => doc.name === event)
                .map((doc) => doc.id)[0] as number,
              categories: cityDocumentTypes
                .map((doc) => {
                  return doc.name === event && doc.categories
                    ? ([...doc.categories] as number[])
                    : ([] as number[]);
                })
                .flat(),
              statuses: cityDocumentTypes
                .map((doc) => {
                  return doc.name === event && doc.statuses
                    ? ([...doc.statuses] as number[])
                    : ([] as number[]);
                })
                .flat(),
            };
          }),
      };
      saveStages([eventTriggerState]);
    },
    [cityDocumentTypes, saveStages],
  );

  const projectStageOptions = useMemo(() => {
    return cityDocumentTypes
      .map(({ name }) => name)
      .filter((option) => (filter?.length ? option.includes(filter) : true))
      .map((option) => {
        return {
          label: option,
          value: option,
        };
      });
  }, [cityDocumentTypes, filter]);

  return (
    <FilterDropdown
      options={projectStageOptions}
      groupByPhase
      onSubmit={(values: string[]) => {
        createEventTriggerState(values);
      }}
      onClearAll={() => saveStages([])}
      checkedValues={selectedTriggers}
      setCheckedValues={saveStages}
      description="Previous project stages on record prior to its most recent activity"
      isExclusion
      setExclusionValues={setExcludeSavedStages}
      exclusionValues={excludedStages}
      onExclusionSubmit={(values: string[]) => {
        setExcludeSavedStages([
          {
            cityId: cityMap.AUSTIN,
            documentTypes: values
              .filter((event) => event !== undefined)
              .map((event) => {
                return {
                  id: cityDocumentTypes
                    .filter((doc) => doc.name === event)
                    .map((doc) => doc.id)[0] as number,
                  categories: cityDocumentTypes
                    .map((doc) => {
                      return doc.name === event && doc.categories
                        ? ([...doc.categories] as number[])
                        : ([] as number[]);
                    })
                    .flat(),
                  statuses: cityDocumentTypes
                    .map((doc) => {
                      return doc.name === event && doc.statuses
                        ? ([...doc.statuses] as number[])
                        : ([] as number[]);
                    })
                    .flat(),
                };
              }),
          },
        ]);
      }}
      onExclusionClearAll={() => setExcludeSavedStages([])}
    >
      Past Project Stages
    </FilterDropdown>
  );
};

export default PastProjectStageFilter;
